import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AuthGate } from '../features/auth/auth-gate.js';
import { useAuth } from '../features/auth/use-auth.js';
import { Button } from '../components/ui/button.js';
import { Input } from '../components/ui/input.js';
import { Label } from '../components/ui/label.js';
import { Badge } from '../components/ui/badge.js';
import { Card, CardHeader, CardTitle, CardDescription } from '../components/ui/card.js';
import { Table, THead, TBody, TR, TH, TD } from '../components/ui/table.js';
import { authFetch } from '../features/auth/auth-fetch.js';

type Role = 'admin' | 'editor' | 'viewer';
// UI exposes only two levels — admin and "felhasználó". Behind the scenes
// "felhasználó" maps to the existing 'editor' role so it can do the day-to-day
// content work (the legacy 'viewer' role is preserved on read for older orgs).
type UiRole = 'admin' | 'user';
const roleToUi = (r: Role): UiRole => (r === 'admin' ? 'admin' : 'user');
const uiToRole = (u: UiRole): Role => (u === 'admin' ? 'admin' : 'editor');
const roleLabel = (r: Role): string => (r === 'admin' ? 'Admin' : 'Felhasználó');

interface AllowlistEntry {
  id: string;
  email: string;
  role: Role;
  invitedBy: string | null;
  revokedAt: string | null;
  createdAt: string;
  hasLoggedIn: boolean;
  userId: string | null;
  name: string | null;
  firstName: string | null;
  lastName: string | null;
}

function AdminUsersInner() {
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<UiRole>('user');
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery<AllowlistEntry[]>({
    queryKey: ['admin', 'users'],
    queryFn: async () => {
      const res = await authFetch('/users?includeRevoked=true');
      if (!res.ok) throw new Error('failed');
      return (await res.json()) as AllowlistEntry[];
    },
  });

  const invite = useMutation({
    mutationFn: async (body: {
      email: string;
      role: Role;
      firstName?: string;
      lastName?: string;
    }) => {
      const res = await authFetch('/users/invite', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { title?: string };
        throw new Error(err.title ?? 'Nem sikerült meghívni a felhasználót');
      }
      return res.json();
    },
    onSuccess: () => {
      setEmail('');
      setFirstName('');
      setLastName('');
      setError(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const updateRole = useMutation({
    mutationFn: async ({ id, role: nextRole }: { id: string; role: Role }) => {
      const res = await authFetch(`/users/${id}/role`, {
        method: 'PATCH',
        body: JSON.stringify({ role: nextRole }),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { title?: string };
        throw new Error(err.title ?? 'Nem sikerült módosítani a szerepkört');
      }
      return res.json();
    },
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const revoke = useMutation({
    mutationFn: async (id: string) => {
      const res = await authFetch(`/users/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { title?: string };
        throw new Error(err.title ?? 'Nem sikerült visszavonni');
      }
    },
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div className="space-y-6">
      <div>
        <div className="h2-eyebrow">Csapat</div>
        <h1 className="mt-1.5 font-sans text-h1 font-normal leading-none tracking-[-0.02em] text-[var(--ink-700)]">
          Felhasználók
        </h1>
        <p className="mt-2 text-body text-[var(--fg-2)]">
          Az adminok minden funkciót látnak és módosíthatnak. A felhasználók a hoteleket, terveket
          és jóváhagyásokat kezelhetik — az admin menüpontokat (Folyamatok, Felhasználás, AI
          beállítások, Csapat) nem látják.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Felhasználó meghívása</CardTitle>
          <CardDescription>
            Csak engedélyezett email-címek kérhetnek bejelentkezési linket.
          </CardDescription>
        </CardHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            invite.mutate({
              email,
              role: uiToRole(role),
              ...(firstName ? { firstName } : {}),
              ...(lastName ? { lastName } : {}),
            });
          }}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="invite-last-name">Vezetéknév</Label>
              <Input
                id="invite-last-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Pl. Kovács"
              />
            </div>
            <div>
              <Label htmlFor="invite-first-name">Keresztnév</Label>
              <Input
                id="invite-first-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Pl. Anna"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_180px_auto]">
            <div>
              <Label htmlFor="invite-email">Email cím</Label>
              <Input
                id="invite-email"
                type="email"
                value={email}
                required
                onChange={(e) => setEmail(e.target.value)}
                placeholder="kollega@morgens.hu"
              />
            </div>
            <div>
              <Label htmlFor="invite-role">Szerepkör</Label>
              <select
                id="invite-role"
                value={role}
                onChange={(e) => setRole(e.target.value as UiRole)}
                className="flex h-10 w-full rounded-md border border-[var(--app-divider)] bg-[var(--app-surface)] px-3 text-body"
              >
                <option value="user">Felhasználó</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button type="submit" disabled={invite.isPending}>
                {invite.isPending ? 'Meghívás…' : 'Meghívás'}
              </Button>
            </div>
          </div>
          {error && <p className="text-body text-red-700">{error}</p>}
        </form>
      </Card>

      <Card className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <THead>
              <TR>
                <TH className="min-w-[12rem] whitespace-nowrap">Név</TH>
                <TH className="w-full min-w-[16rem]">Email</TH>
                <TH className="whitespace-nowrap">Szerepkör</TH>
                <TH className="whitespace-nowrap">Státusz</TH>
                <TH className="whitespace-nowrap">Bejelentkezett?</TH>
                <TH className="whitespace-nowrap text-right">Műveletek</TH>
              </TR>
            </THead>
            <TBody>
              {isLoading && (
                <TR>
                  <TD colSpan={6} className="text-center text-[var(--fg-faint)]">
                    Betöltés…
                  </TD>
                </TR>
              )}
              {data?.map((row) => {
                const isSelf = me?.email?.toLowerCase() === row.email.toLowerCase();
                const ui = roleToUi(row.role);
                const disabled = !!row.revokedAt || isSelf || updateRole.isPending;
                const fullName =
                  row.firstName || row.lastName
                    ? `${row.lastName ?? ''} ${row.firstName ?? ''}`.trim()
                    : row.name;
                return (
                  <TR key={row.id}>
                    <TD>
                      {fullName ?? (
                        <span className="text-meta italic text-[var(--fg-faint)]">—</span>
                      )}
                    </TD>
                    <TD className="w-full">
                      <span>{row.email}</span>
                      {isSelf && (
                        <span className="ml-2 text-meta text-[var(--fg-faint)]">(te)</span>
                      )}
                    </TD>
                    <TD>
                      <div className="flex items-center gap-2">
                        <select
                          value={ui}
                          disabled={disabled}
                          onChange={(e) =>
                            updateRole.mutate({
                              id: row.id,
                              role: uiToRole(e.target.value as UiRole),
                            })
                          }
                          className="h-8 rounded-md border border-[var(--app-divider)] bg-[var(--app-surface)] px-2 text-body disabled:cursor-not-allowed disabled:opacity-60"
                          title={
                            isSelf
                              ? 'A saját szerepköröd nem módosíthatod'
                              : row.revokedAt
                                ? 'Visszavont felhasználó'
                                : 'Szerepkör módosítása'
                          }
                        >
                          <option value="user">Felhasználó</option>
                          <option value="admin">Admin</option>
                        </select>
                        <Badge tone={row.role === 'admin' ? 'success' : 'neutral'}>
                          {roleLabel(row.role)}
                        </Badge>
                      </div>
                    </TD>
                    <TD>
                      {row.revokedAt ? (
                        <Badge tone="danger">Visszavonva</Badge>
                      ) : (
                        <Badge tone="success">Aktív</Badge>
                      )}
                    </TD>
                    <TD>{row.hasLoggedIn ? '✓' : '–'}</TD>
                    <TD className="whitespace-nowrap text-right">
                      {!row.revokedAt && !isSelf && (
                        <Button
                          size="sm"
                          variant="destructiveSoft"
                          onClick={() => revoke.mutate(row.id)}
                          disabled={revoke.isPending}
                        >
                          Visszavonás
                        </Button>
                      )}
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}

function AdminUsersPage() {
  return (
    <AuthGate roles={['admin']}>
      <AdminUsersInner />
    </AuthGate>
  );
}

export const Route = createFileRoute('/app/admin/users')({
  component: AdminUsersPage,
});
